import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useRef, useState } from "react";
import api from "@/lib/api";
import { trackOutboundClick } from "@/hooks/useAnalytics";
import { SeoHead } from "@/components/public/SeoHead";
import { JsonLd } from "@/components/public/JsonLd";
import { ClassificationBadge } from "@/components/public/ClassificationBadge";
import { ClothingItemCard } from "@/components/public/ClothingItemCard";
import { AdSlot } from "@/components/public/AdSlot";
import { ExternalLink, AlertTriangle, ChevronLeft, ChevronLeftIcon, ChevronRightIcon, Info, Quote } from "lucide-react";
import { effectiveClassification, formatDate, classificationClass } from "@/lib/format";
import { useConvertedPrice } from "@/lib/currency";

export default function ClothingDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ["clothing-detail", slug],
    queryFn: async () => (await api.get(`/public/clothing/${slug}`)).data,
    retry: false,
  });
  const convertedPrice = useConvertedPrice(data?.item?.price, data?.item?.currency);

  if (isLoading) return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-muted_text">Loading…</div>;
  if (error || !data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-14 text-center">
        <h1 className="editorial-headline text-5xl mb-4">Item not found</h1>
        <Link to="/clothing" className="text-forest underline">Back to clothing</Link>
      </div>
    );
  }
  const item = data.item;
  const gallery = [item.image_url, ...(item.image_gallery || [])].filter(Boolean);

  // JSON-LD structured data
  const origin = typeof window !== "undefined" ? window.location.origin : "https://sustainablycurated.com";
  const itemUrl = `${origin}/clothing/${slug}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Product",
      name: item.name,
      description: item.product_description,
      image: gallery.length ? gallery : (item.image_url || undefined),
      brand: { "@type": "Brand", name: item.brand_name },
      material: (item.materials || []).map((m) => m.material_name).filter(Boolean).join(", ") || undefined,
      category: item.clothing_item_type_name || undefined,
      url: itemUrl,
      offers: item.price != null ? {
        "@type": "Offer",
        price: item.price,
        priceCurrency: item.currency || "EUR",
        availability: "https://schema.org/InStock",
        url: item.product_url || itemUrl,
      } : undefined,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: origin + "/" },
        { "@type": "ListItem", position: 2, name: "Clothing", item: origin + "/clothing" },
        { "@type": "ListItem", position: 3, name: item.brand_name, item: item.brand_slug ? `${origin}/brands/${item.brand_slug}` : undefined },
        { "@type": "ListItem", position: 4, name: item.name, item: itemUrl },
      ].filter((b) => b.item),
    },
  ];

  return (
    <>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="clothing-detail-page">
      <SeoHead
        title={item.seo_title || `${item.name} by ${item.brand_name} — Sustainably Curated`}
        description={item.seo_description || item.product_description || `${item.name} from ${item.brand_name}, reviewed by Sustainably Curated.`}
        path={`/clothing/${slug}`}
        image={item.image_url}
      />
      <JsonLd data={jsonLd} />

      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-muted_text hover:text-forest mb-6" data-testid="clothing-back-button">
        <ChevronLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Gallery — image keeps natural aspect ratio, capped at 500px tall */}
        <div className="md:flex-shrink-0 space-y-2">
          {gallery[0] && (
            <div className="relative inline-block">
              <img
                src={gallery[0]}
                alt={item.name}
                className="max-h-[500px] w-auto max-w-full object-contain bg-champagne/30"
              />
              {item.is_featured && (
                <span
                  className="absolute top-3 left-3 text-[10px] font-semibold uppercase tracking-wider rounded-full px-2.5 py-1 bg-leaf/15 text-leaf shadow-sm"
                  data-testid="detail-featured-pill"
                >
                  Featured
                </span>
              )}
            </div>
          )}
          {gallery.length > 1 && (
            <div className="grid grid-cols-4 gap-2">
              {gallery.slice(1).map((u, i) => (
                <img key={i} src={u} alt="" className="aspect-square object-cover" loading="lazy" />
              ))}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.2em] text-muted_text mb-2">
            <Link
              to={`/brands/${item.brand_slug || ""}`}
              className="underline underline-offset-4 decoration-muted_text/40 hover:decoration-forest hover:text-forest transition-colors"
              data-testid="clothing-brand-link"
            >
              {item.brand_name}
            </Link>
            {item.clothing_item_type_name && <span> · {item.clothing_item_type_name}</span>}
          </div>
          <h1 className="editorial-headline text-2xl sm:text-3xl mb-4">{item.name}</h1>

          <div className="flex flex-wrap items-center gap-3 mb-2">
            {effectiveClassification(item.materials) && (
              <span
                className={`inline-flex items-center justify-center rounded-full border h-10 px-5 text-sm font-semibold uppercase tracking-wider ${classificationClass(effectiveClassification(item.materials))}`}
                data-testid={`classification-${effectiveClassification(item.materials).toLowerCase()}`}
              >
                {effectiveClassification(item.materials)}
              </span>
            )}
            {item.product_url && (
              <a
                href={item.product_url}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-1.5 text-sm bg-forest text-ivory rounded-full px-5 h-10 hover:bg-forest/90"
                data-testid="clothing-original-link"
                onClick={() => trackOutboundClick({ target_type: "clothing", target_slug: item.slug, destination_url: item.product_url })}
                onAuxClick={() => trackOutboundClick({ target_type: "clothing", target_slug: item.slug, destination_url: item.product_url })}
              >
                View on brand <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
            {item.price != null && convertedPrice && (
              <span className="text-base" data-testid="detail-price-converted">
                {convertedPrice}
              </span>
            )}
          </div>
          <p className="text-[11px] text-muted_text mt-3 mb-4" data-testid="price-disclaimer">
            Price is rounded to the nearest integer and reflects what was captured on the last crawl - the brand may have changed it since. <strong>Last Reviewed: {formatDate(item.reviewed_at)}</strong>
          </p>

          {item.product_description && (
            <section className="mb-6" data-testid="item-description-from-brand">
              <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted_text mb-3">
                <Quote className="w-3.5 h-3.5" />
                <span>Description from the brand's website</span>
              </div>
              <blockquote className="border-l-2 border-forest/30 pl-4 text-sm text-ink/85 leading-relaxed italic whitespace-pre-line">
                {item.product_description}
              </blockquote>
            </section>
          )}

          {/* Materials */}
          {item.materials?.length > 0 && (
            <section className="mb-6">
              <div className="flex items-baseline gap-3 flex-wrap mb-3">
                <h2 className="font-serif text-2xl">Material composition</h2>
                <Link
                  to="/methodology"
                  className="text-xs text-forest underline underline-offset-4 hover:text-leaf"
                  data-testid="clothing-methodology-link"
                >
                  How we score →
                </Link>
                {item.label_verified && <span className="text-xs text-leaf">✓ Label verified</span>}
              </div>
              {item.false_advertising_confirmed ? (
                <div className="border-2 border-clay bg-clay/15 rounded-2xl p-4 mb-3 flex items-start gap-2" data-testid="item-materials-mislabeled">
                  <AlertTriangle className="w-4 h-4 text-clay mt-0.5" />
                  <div className="text-sm text-clay">
                    <strong>Materials mislabeled.</strong> <span className="text-ink/85">The brand's stated material composition doesn't match the actual product — verified by our editors.</span>
                  </div>
                </div>
              ) : item.false_advertising_suspected ? (
                <div className="border-2 border-amber bg-amber/15 rounded-2xl p-4 mb-3 flex items-start gap-2" data-testid="item-materials-disputed">
                  <AlertTriangle className="w-4 h-4 text-amber mt-0.5" />
                  <div className="text-sm text-amber">
                    <strong>Materials disputed.</strong> <span className="text-ink/85">The brand's stated material composition may not match the actual product. Under editorial review.</span>
                  </div>
                </div>
              ) : null}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {item.materials.map((m, i) => {
                  const effective = m.override_classification || m.material_classification;
                  const isOverridden = m.override_classification && m.override_classification !== m.material_classification;
                  const certs = m.certifications || [];
                  return (
                    <div key={i} className="border border-line rounded-lg p-3 space-y-1.5 hover:border-forest/40 transition-colors">
                      <div className="flex items-center justify-between gap-2">
                        <Link to={`/materials/${m.material_slug || ""}`} className="flex items-center gap-3 flex-1 min-w-0">
                          <ClassificationBadge classification={effective} className="text-[10px] w-20 justify-center shrink-0" />
                          <span className="text-sm truncate">{m.material_name}</span>
                        </Link>
                        <span className="text-xs text-muted_text shrink-0">{m.percentage != null ? `${m.percentage}%` : ""}</span>
                      </div>
                      {certs.length > 0 && (
                        <div className="flex items-center gap-2 min-w-0">
                          {certs.map((c) => (
                            <Link key={c.id} to={`/materials-certifications/${c.slug}`} className="inline-flex items-center gap-1 bg-leaf/10 border border-leaf/30 text-[11px] px-2 py-0.5 rounded-full hover:border-leaf/60 shrink-0 w-20 justify-center">
                              {c.name}
                            </Link>
                          ))}
                          <span className="text-[10px] text-muted_text italic truncate pr-1">
                            {isOverridden
                              ? `Reclassified from ${m.material_classification} thanks to certification`
                              : "Material certified"}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

        </div>
      </div>

      {item.override_reason && item.sustainability_status === "approved_featured" && (() => {
        const hasNonGreen = (item.materials || []).some(
          (m) => (m.override_classification || m.material_classification) !== "GREEN"
        );
        return (
          <section
            className={`rounded-2xl p-5 mt-8 border ${hasNonGreen ? "border-amber/40 bg-amber/5" : "border-leaf/40 bg-leaf/5"}`}
            data-testid="item-editorial-disclaimer"
          >
            <div className="flex items-start gap-3">
              <Info className={`w-4 h-4 mt-0.5 flex-shrink-0 ${hasNonGreen ? "text-amber" : "text-leaf"}`} />
              <div>
                <h2 className="font-serif text-xl mb-1">Editorial review</h2>
                <p className="text-sm text-ink/85 whitespace-pre-line">{item.override_reason}</p>
                {hasNonGreen && (
                  <p className="text-[11px] text-muted_text mt-2">
                    This item contains some non-GREEN materials. We&apos;ve approved it for the reason above.
                  </p>
                )}
              </div>
            </div>
          </section>
        );
      })()}

      {item.editor_images && item.editor_images.length > 0 && (
        <EditorImagesRow images={item.editor_images} />
      )}
      </div>

      <AdSlot slotKey="clothing_detail" />

      {data.related?.length > 0 && (
        <div className="border-t border-line" data-testid="clothing-related-separator">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 pb-8" data-testid="clothing-related">
          <div className="flex items-end justify-between mb-5">
            <h2 className="font-serif text-3xl">More from {item.brand_name}</h2>
            {item.brand_id && (
              <Link
                to={`/clothing?brand_id=${item.brand_id}`}
                className="text-sm text-forest underline underline-offset-4 whitespace-nowrap"
                data-testid="clothing-related-see-all"
              >
                See all →
              </Link>
            )}
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {data.related.slice(0, 5).map((r) => (
              <ClothingItemCard key={r.id} item={r} />
            ))}
          </div>
        </section>
        </div>
      )}
    </>
  );
}

function EditorImagesRow({ images }) {
  const scrollerRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const showArrows = images.length > 5;

  function updateEdges() {
    const el = scrollerRef.current;
    if (!el) return;
    setAtStart(el.scrollLeft <= 1);
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
  }
  function scrollByCards(dir) {
    const el = scrollerRef.current;
    if (!el) return;
    // 208px image + 12px gap (gap-3) — scroll by ~2 cards at a time
    el.scrollBy({ left: dir * (208 + 12) * 2, behavior: "smooth" });
  }

  return (
    <section className="mt-8 relative" data-testid="item-editor-images">
      <div className="text-xs uppercase tracking-[0.2em] text-muted_text mb-3">Editor&apos;s images</div>
      <div className="relative">
        <div
          ref={scrollerRef}
          onScroll={updateEdges}
          className={`flex gap-3 ${showArrows ? "overflow-x-auto scroll-smooth snap-x snap-mandatory" : "flex-wrap"}`}
          style={showArrows ? { scrollbarWidth: "none" } : undefined}
        >
          {images.map((img, i) => (
            <div
              key={i}
              className="w-52 aspect-[3/4] overflow-hidden rounded-2xl border border-line bg-champagne/30 shrink-0 snap-start"
              data-testid={`item-editor-image-${i}`}
            >
              <img src={img.url} alt="" loading="lazy" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>

        {showArrows && (
          <>
            <button
              type="button"
              onClick={() => scrollByCards(-1)}
              disabled={atStart}
              aria-label="Previous images"
              className="absolute top-1/2 -translate-y-1/2 left-1 bg-ivory/95 hover:bg-ivory border border-line rounded-full p-1.5 shadow-sm disabled:opacity-30 disabled:pointer-events-none"
              data-testid="editor-images-prev"
            >
              <ChevronLeftIcon className="w-4 h-4 text-ink" />
            </button>
            <button
              type="button"
              onClick={() => scrollByCards(1)}
              disabled={atEnd}
              aria-label="Next images"
              className="absolute top-1/2 -translate-y-1/2 right-1 bg-ivory/95 hover:bg-ivory border border-line rounded-full p-1.5 shadow-sm disabled:opacity-30 disabled:pointer-events-none"
              data-testid="editor-images-next"
            >
              <ChevronRightIcon className="w-4 h-4 text-ink" />
            </button>
          </>
        )}
      </div>
    </section>
  );
}

